import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  getPartnerStoreInvoicesQuery,
  getAccountLedgersForStoreQuery,
} from '../../graphql/queries';

import { useContext, useState } from 'react';
import {
  CanAccessPartnerData,
  ViewerContext,
  IsFinance,
  IsAdmin,
} from '../../providers/ViewerContextProvider';
import { updateStoreSuperAdminMutation } from '../../graphql/mutations';
import { useStoreQuery } from '../../graphql/queries/storeAdminInfo.generated';
import { useGetLastAccountLedgerProcessedConfigQuery } from '../../graphql/queries/getLastAccountLedgerProcessedConfig.generated';
import { useUpdateAccountLedgerMutation } from '../../graphql/mutations/updateAccountLedger.generated';
import { ContentSection } from '../../components/Layout/ContentSection';
import { Communicator } from '../../tools/Communication';
import { InvoiceList } from './InvoiceList';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaEdit,
  FaSave,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import { formatBillomatPrice } from '@/utils/formatter';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  ButtonGroup,
  Badge,
} from 'reactstrap';

type InvoiceActionProps = {
  storeId?: string | null;
  match?: { params: { storeId: string } };
  title: string;
};

export const StoreInvoiceList = function ({
  storeId,
  match,
  title,
}: InvoiceActionProps) {
  const sId = match?.params.storeId || storeId;

  const viewer = useContext(ViewerContext);
  const allowedToAccess = CanAccessPartnerData(viewer);
  const isFinanceAndNotAdmin = IsFinance(viewer) && !IsAdmin(viewer);

  const [invoices, setInvoices] = useState([] as any[]);
  const [showAll, setShowAll] = useState(false);
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState<any>(null);
  const [hideComment, setHideComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentValue, setEditingCommentValue] = useState('');
  const [isLedgerSectionExpanded, setIsLedgerSectionExpanded] = useState(false);
  const [storeIntegrationToken, setStoreIntegrationToken] = useState<
    string | null
  >(null);

  const {
    loading,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useQuery(getPartnerStoreInvoicesQuery, {
    variables: { store_id: sId },
    onCompleted: (partnerInvoiceData) => {
      if (
        partnerInvoiceData != null &&
        Array.isArray(partnerInvoiceData.getPartnerStoreInvoices)
      ) {
        // @ts-ignore
        setInvoices(
          [...partnerInvoiceData.getPartnerStoreInvoices]
            .sort((invoiceA: any, invoiceB: any) => invoiceB.id - invoiceA.id)
            .filter(
              (invoice: any) => allowedToAccess || invoice.status !== 'DRAFT',
            ) as any[],
        );
      }
    },
  });
  const [updateStoreSuperAdmin] = useMutation(updateStoreSuperAdminMutation);

  const { data: storeQueryResult } = useStoreQuery({
    variables: { id: sId || '' },
    skip: sId == null,
  });

  const store = storeQueryResult?.store;

  // Fetch a store integration token so partners can download the billed
  // containers CSV from the (store/integration-token protected) REST endpoint.
  // The endpoint expects the internal MerchantStores GUID, which the store query
  // exposes as `external_vytal_id` (sId is the external pickpack id, not a GUID).
  const storeVytalId = store?.external_vytal_id;
  React.useEffect(() => {
    if (!storeVytalId) return;
    (async () => {
      try {
        const tokenResponse = await Communicator.Get(
          '/Merchant/GetIntegrationTokens',
          { storeId: storeVytalId },
        );
        const tokenBody = await tokenResponse.json();
        setStoreIntegrationToken(tokenBody.tokens?.other ?? null);
      } catch (e) {
        console.error('Failed to fetch store integration token', e);
        setStoreIntegrationToken(null);
      }
    })();
  }, [storeVytalId]);

  // Fetch last processed account ledger config
  const { data: lastProcessedConfig } =
    useGetLastAccountLedgerProcessedConfigQuery({
      skip: !allowedToAccess,
    });

  // Update account ledger mutation
  const [updateAccountLedger] = useUpdateAccountLedgerMutation();

  // Fetch all account ledgers
  const {
    data: allAccountLedgersData,
    loading: loadingAccountLedgers,
    error: accountLedgersError,
  } = useQuery(getAccountLedgersForStoreQuery, {
    variables: { store_id: sId },
    skip: !allowedToAccess || sId == null,
    errorPolicy: 'all', // Return data even if there are errors
  });

  const allAccountLedgers = React.useMemo(
    () => allAccountLedgersData?.getAccountLedgersForStore || [],
    [allAccountLedgersData],
  );

  // Filter visible ledgers based on showAll toggle
  const visibleLedgers = React.useMemo(() => {
    if (showAll) {
      // Show all ledgers (linked, non-linked, hidden, non-hidden)
      return allAccountLedgers;
    } else {
      // Show only non-hidden, non-linked ledgers
      return allAccountLedgers.filter(
        (ledger: any) =>
          !ledger.hidden_at && ledger.billomat_invoice_id === null,
      );
    }
  }, [allAccountLedgers, showAll]);

  // Check if balance calculation is loading
  const isBalanceLoading = loading || loadingAccountLedgers;

  // Calculate Partner Store Balance
  const partnerStoreBalance = React.useMemo(() => {
    // Sum up invoice total_gross amounts, excluding DRAFT and PAID status
    const invoiceTotal = invoices.reduce((sum, invoice) => {
      if (invoice.status !== 'OVERDUE' && invoice.status !== 'OPEN') {
        return sum;
      }
      return sum + (parseFloat(invoice.total_gross) || 0);
    }, 0);

    // Sum up non-hidden account ledgers: add credit_amount, subtract debit_amount
    const ledgerTotal = allAccountLedgers.reduce((sum: number, ledger: any) => {
      // Skip hidden ledgers
      if (ledger.hidden_at) {
        return sum;
      }
      // skip account ledgers that are linked to a billomat invoice
      if (ledger.billomat_invoice_id !== null) {
        return sum;
      }
      const credit = parseFloat(ledger.credit_amount) || 0;
      const debit = parseFloat(ledger.debit_amount) || 0;
      return sum + credit - debit;
    }, 0);

    // Multiply total by -1
    return (invoiceTotal + ledgerTotal) * -1;
  }, [invoices, allAccountLedgers]);

  // Handle hide/unhide ledger
  const handleHideLedger = (ledger: any) => {
    setSelectedLedger(ledger);
    setHideComment(ledger.comment || '');
    setHideModalOpen(true);
  };

  const handleConfirmHide = async () => {
    try {
      await updateAccountLedger({
        variables: {
          input: {
            id: selectedLedger.id,
            is_hidden: true,
            comment: hideComment.trim() || null,
          },
        },
        refetchQueries: [
          {
            query: getAccountLedgersForStoreQuery,
            variables: { store_id: sId },
          },
        ],
      });
      setHideModalOpen(false);
      setSelectedLedger(null);
      setHideComment('');
    } catch (error: any) {
      alert('Error hiding account ledger: ' + error.message);
    }
  };

  const handleStartEditComment = (ledger: any) => {
    setEditingCommentId(ledger.id);
    setEditingCommentValue(ledger.comment || '');
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentValue('');
  };

  const handleSaveComment = async (ledger: any) => {
    try {
      await updateAccountLedger({
        variables: {
          input: {
            id: ledger.id,
            comment: editingCommentValue.trim() || null,
          },
        },
        refetchQueries: [
          {
            query: getAccountLedgersForStoreQuery,
            variables: { store_id: sId },
          },
        ],
      });
      setEditingCommentId(null);
      setEditingCommentValue('');
    } catch (error: any) {
      alert('Error updating comment: ' + error.message);
    }
  };

  const handleUnhideLedger = async (ledger: any) => {
    try {
      await updateAccountLedger({
        variables: {
          input: {
            id: ledger.id,
            is_hidden: false,
          },
        },
        refetchQueries: [
          {
            query: getAccountLedgersForStoreQuery,
            variables: { store_id: sId },
          },
        ],
      });
    } catch (error: any) {
      alert('Error unhiding account ledger: ' + error.message);
    }
  };

  // @ts-ignore
  return (
    <ContentSection title={title} collapsable={false} key="content">
      {allowedToAccess && (
        <React.Fragment>
          {/* Partner Store Balance - Always Visible */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '0.25rem',
              border: '1px solid #dee2e6',
            }}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FaLock />
              <strong style={{ fontSize: '1.1rem' }}>
                {i18n.t('AccountLedger:partnerStoreBalance')}:
              </strong>
              <Badge
                color="info"
                pill
                style={{ fontSize: '0.6rem', padding: '0.2rem 0.4rem' }}
              >
                BETA
              </Badge>
            </div>
            <div
              style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: partnerStoreBalance < 0 ? '#dc3545' : '#28a745',
              }}
            >
              {isBalanceLoading ? (
                <span style={{ fontSize: '1rem' }}>
                  {i18n.t('AccountLedger:loading')}...
                </span>
              ) : (
                <>
                  {formatBillomatPrice(
                    Math.abs(partnerStoreBalance),
                    store?.currency_symbol || '€',
                  )}
                  {partnerStoreBalance !== 0 && (
                    <span
                      style={{ fontSize: '0.875rem', marginLeft: '0.5rem' }}
                    >
                      {partnerStoreBalance < 0
                        ? i18n.t('AccountLedger:partnerOwesUs')
                        : i18n.t('AccountLedger:weOwePartner')}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
          {/* Collapsable Non-Linked Account Ledgers Section */}
          <div style={{ marginBottom: '1rem' }}>
            <Button
              color="link"
              onClick={() =>
                setIsLedgerSectionExpanded(!isLedgerSectionExpanded)
              }
              style={{
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                textDecoration: 'none',
              }}
            >
              {isLedgerSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
              <FaLock style={{ marginRight: '5px' }} />
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                {i18n.t('AccountLedger:nonLinkedAccountLedgers')}
                {!loadingAccountLedgers && (
                  <Badge color="secondary" pill>
                    {visibleLedgers.length}
                  </Badge>
                )}
              </h4>
            </Button>

            {isLedgerSectionExpanded && (
              <div style={{ marginTop: '1rem' }}>
                {/* Last Processed Info */}
                {lastProcessedConfig?.getLastAccountLedgerProcessedConfig && (
                  <div className="alert alert-info mb-3" role="alert">
                    <strong>
                      <FaLock style={{ marginRight: '5px' }} />
                      {i18n.t('AccountLedger:lastProcessedInfo')}:
                    </strong>{' '}
                    {i18n.t('AccountLedger:lastBatchNumber')}:{' '}
                    {
                      lastProcessedConfig.getLastAccountLedgerProcessedConfig
                        .last_account_ledger_batch_number
                    }
                    {' | '}
                    {i18n.t('AccountLedger:lastProcessedAt')}:{' '}
                    {new Date(
                      lastProcessedConfig.getLastAccountLedgerProcessedConfig.last_account_ledger_processed_at,
                    ).toLocaleDateString(
                      i18n.language,
                      getDateFormatOptions(true),
                    )}
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  {/* Toggle Button */}
                  <ButtonGroup size="sm">
                    <Button
                      color={!showAll ? 'primary' : 'secondary'}
                      onClick={() => setShowAll(false)}
                      active={!showAll}
                    >
                      {i18n.t('AccountLedger:showNonMatched')}
                    </Button>
                    <Button
                      color={showAll ? 'primary' : 'secondary'}
                      onClick={() => setShowAll(true)}
                      active={showAll}
                    >
                      {i18n.t('AccountLedger:showAll')}
                    </Button>
                  </ButtonGroup>
                </div>

                {accountLedgersError && (
                  <div className="alert alert-danger" role="alert">
                    <strong>Error loading account ledgers:</strong>{' '}
                    {accountLedgersError.message}
                  </div>
                )}

                <div>
                  <table className="bg-light table w-auto">
                    <thead>
                      <tr>
                        <th>
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <FaLock style={{ marginRight: '5px' }} />
                            {i18n.t('AccountLedger:date')}
                          </div>
                        </th>
                        <th>
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <FaLock style={{ marginRight: '5px' }} />
                            {i18n.t('AccountLedger:debitorAccountNumber')}
                          </div>
                        </th>
                        <th>
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <FaLock style={{ marginRight: '5px' }} />
                            {i18n.t('AccountLedger:statementDescriptor')}
                          </div>
                        </th>
                        <th>
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <FaLock style={{ marginRight: '5px' }} />
                            {i18n.t('AccountLedger:invoiceReference')}
                          </div>
                        </th>
                        <th>
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <FaLock style={{ marginRight: '5px' }} />
                            {i18n.t('AccountLedger:amount')}
                          </div>
                        </th>
                        <th>
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <FaLock style={{ marginRight: '5px' }} />
                            {i18n.t('AccountLedger:comment')}
                          </div>
                        </th>
                        {isFinanceAndNotAdmin && (
                          <th>
                            <div
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <FaLock style={{ marginRight: '5px' }} />
                              {i18n.t('AccountLedger:actions')}
                            </div>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLedgers.map((ledger: any) => (
                        <tr key={ledger.id}>
                          <td>
                            {ledger.booking_date
                              ? new Date(
                                  ledger.booking_date,
                                ).toLocaleDateString(
                                  i18n.language,
                                  getDateFormatOptions(false),
                                )
                              : '-'}
                          </td>
                          <td>{ledger.debitor_account_number}</td>
                          <td>{ledger.statement_descriptor}</td>
                          <td>{ledger.invoice_reference}</td>
                          <td className="numbers-formatted">
                            {ledger.credit_amount != null
                              ? `- ${formatBillomatPrice(ledger.credit_amount, store?.currency_symbol || '€')}`
                              : ledger.debit_amount != null
                                ? formatBillomatPrice(
                                    ledger.debit_amount,
                                    store?.currency_symbol || '€',
                                  )
                                : '-'}
                          </td>
                          <td>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                              }}
                            >
                              {ledger.hidden_at && (
                                <span className="badge badge-warning">
                                  {i18n.t('AccountLedger:hidden')}
                                </span>
                              )}
                              {ledger.billomat_invoice_id !== null && (
                                <span className="badge badge-success">
                                  Linked
                                </span>
                              )}
                              {editingCommentId === ledger.id ? (
                                <>
                                  <Input
                                    type="text"
                                    value={editingCommentValue}
                                    onChange={(e) =>
                                      setEditingCommentValue(e.target.value)
                                    }
                                    style={{ minWidth: '200px' }}
                                    autoFocus
                                  />
                                  <Button
                                    size="sm"
                                    color="success"
                                    onClick={() => handleSaveComment(ledger)}
                                  >
                                    <FaSave />
                                  </Button>
                                  <Button
                                    size="sm"
                                    color="secondary"
                                    onClick={handleCancelEditComment}
                                  >
                                    <FaTimes />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <span>{ledger.comment || '-'}</span>
                                  {isFinanceAndNotAdmin && (
                                    <Button
                                      size="sm"
                                      color="link"
                                      onClick={() =>
                                        handleStartEditComment(ledger)
                                      }
                                      style={{ padding: '0', minWidth: 'auto' }}
                                    >
                                      <FaEdit />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                          {isFinanceAndNotAdmin && (
                            <td>
                              {ledger.hidden_at ? (
                                <Button
                                  size="sm"
                                  color="success"
                                  onClick={() => handleUnhideLedger(ledger)}
                                >
                                  <FaEye /> {i18n.t('AccountLedger:unhide')}
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  color="warning"
                                  onClick={() => handleHideLedger(ledger)}
                                >
                                  <FaEyeSlash /> {i18n.t('AccountLedger:hide')}
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}

                      {loadingAccountLedgers && (
                        <tr>
                          <td colSpan={6 + (isFinanceAndNotAdmin ? 1 : 0)}>
                            loading...
                          </td>
                        </tr>
                      )}
                      {!loadingAccountLedgers &&
                        visibleLedgers.length === 0 && (
                          <tr>
                            <td colSpan={6 + (isFinanceAndNotAdmin ? 1 : 0)}>
                              {i18n.t(
                                'AccountLedger:noNonLinkedAccountLedgers',
                              )}
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      )}

      {invoicesError && (
        <div className="alert alert-danger" role="alert">
          <strong>{i18n.t('AccountLedger:errorLoadingInvoices')}:</strong>{' '}
          {invoicesError.message}
        </div>
      )}

      <InvoiceList
        allowedToAccess={allowedToAccess}
        invoiceTag={sId || ''}
        invoices={invoices}
        loading={loading}
        setInvoices={setInvoices}
        currencySymbol={store?.currency_symbol}
        isFinanceAndNotAdmin={isFinanceAndNotAdmin}
        onRefresh={() => refetchInvoices()}
        storeId={sId}
        storeIntegrationToken={storeIntegrationToken}
        onSave={() =>
          updateStoreSuperAdmin({
            variables: {
              id: sId,
              store: {
                hidden_invoice_ids: invoices
                  .filter((i) => i.hidden)
                  .map((i) => i.id),
              },
            },
            refetchQueries: [
              {
                query: getPartnerStoreInvoicesQuery,
                variables: { store_id: sId },
              },
            ],
          }).catch((err: any) => {
            alert('Error updating store as super admin: ' + err);
          })
        }
      />

      {/* Hide Account Ledger Modal */}
      <Modal isOpen={hideModalOpen} toggle={() => setHideModalOpen(false)}>
        <ModalHeader toggle={() => setHideModalOpen(false)}>
          {i18n.t('AccountLedger:hideAccountLedger')}
        </ModalHeader>
        <ModalBody>
          <Label>
            {i18n.t('AccountLedger:comment')} (
            {i18n.t('AccountLedger:optional')})
          </Label>
          <Input
            type="textarea"
            value={hideComment}
            onChange={(e) => setHideComment(e.target.value)}
            placeholder={i18n.t('AccountLedger:enterComment')}
            rows={4}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setHideModalOpen(false)}>
            {i18n.t('AccountLedger:cancel')}
          </Button>
          <Button color="primary" onClick={handleConfirmHide}>
            {i18n.t('AccountLedger:hide')}
          </Button>
        </ModalFooter>
      </Modal>
    </ContentSection>
  );
};
